
import { Component, Vue, Ref } from 'vue-property-decorator'
import { AiFeatureService, AiService, AiBatchCheckService } from '@/services/index'
import { UserModule } from '@/store/modules/user'
import { IAiFeature, IUpsertFeatureParams, IAiProviderType } from '@/types/ai-types'
import EditDialog from './editDialog.vue'

enum Role {
    Admin = 1,
    Super = 2
}

/** 进度轮询周期（ms） */
const POLL_INTERVAL_MS = 3000

@Component({
    name: 'AiFeatureList',
    components: {
        EditDialog
    }
})
export default class extends Vue {
    @Ref('editDialogRef') private editDialog!: EditDialog

    private features: IAiFeature[] = []
    private allFeatures: IAiFeature[] = []
    /** 模型类型映射：用于把 provider_type code 翻译成中文 */
    private providerTypes: IAiProviderType[] = []

    private pageIndex = 1
    private pageSize = 20
    private pageTotal = 0
    private loading = true
    private searchInput = ''
    private curStatus: number | '' = ''
    /** 媒体存在性排查的功能标识（与 /feature-keys 接口中的 key 对齐） */
    private MEDIA_EXIST_CHECK_KEY = 'media_exist_check'

    /* ===========================================================
     *                  全域排查：参数配置弹窗
     * =========================================================== */
    private batchConfigDialog = {
        visible: false,
        loading: false,
        form: {
            batchSize: -1,
            maxMedia: -1
        },
        rules: {
            batchSize: [
                { required: true, message: '请输入单次AI询问信源数', trigger: 'blur' }
            ],
            maxMedia: [
                { required: true, message: '请输入最大排查媒体数', trigger: 'blur' }
            ]
        }
    }

    /* ===========================================================
     *                  全域排查：进度弹窗状态
     * =========================================================== */
    private batchProgressDialog = {
        visible: false,
        task: {
            taskId: '',
            total: 0,
            pending: 0,
            success: 0,
            failed: 0,
            skipped: 0,
            failedDetails: [] as string[],
            stopFlag: false,
            startTime: 0,
            status: '',
            elapsedSec: 0,
            etaSec: 0
        },
        // 停止按钮的 loading
        stopping: false,
        errorMsg: ''
    }
    /** 轮询 timer */
    private pollTimer: number | null = null
    /** 每秒刷新已用时间的 timer（与 pollTimer 解耦，独立启停） */
    private tickTimer: number | null = null

    private typeTagMap: any = {
        deepseek: '',
        openai: 'success',
        qwen: 'warning',
        alibaba: 'warning',
        baidu: 'info',
        bytedance: 'danger',
        tencent: '',
        huawei: 'info',
        custom: 'info'
    }

    /** 可见：管理员(1) 及以上 */
    private get canManage() {
        return UserModule.role >= Role.Admin
    }
    /** 写类操作（新建/编辑/删除）：仅超级管理员(2) */
    private get canWrite() {
        return UserModule.role === Role.Super
    }

    private async mounted() {
        await this.getProviderTypes()
        await this.getFeatures()
    }

    /* ===========================================================
     *                          数据加载
     * =========================================================== */

    private async getProviderTypes() {
        try {
            const result = await AiService.listProviderTypes()
            if (result.status === 200) {
                this.providerTypes = result.data || []
            }
        } catch {
            this.providerTypes = []
        }
    }

    private async getFeatures() {
        const params: any = {
            keyword: this.searchInput || undefined,
            status: this.curStatus === '' ? undefined : this.curStatus,
            page: this.pageIndex,
            size: this.pageSize
        }
        this.loading = true
        try {
            const result = await AiFeatureService.listFeatures(params)
            if (result.status === 200) {
                const data = result.data || {}
                this.allFeatures = data.rows || data.list || []
                this.pageTotal = data.total || 0
            }
        } finally {
            this.loading = false
            this.applyLocalFilter()
        }
    }

    /** 本地二次筛选（在服务端筛选基础上按前端输入补刀） */
    private applyLocalFilter() {
        let list = [...this.allFeatures]
        if (this.curStatus !== '') {
            list = list.filter(i => i.status === this.curStatus)
        }
        if (this.searchInput) {
            const kw = this.searchInput.toLowerCase()
            list = list.filter(i =>
                (i.feature_key && i.feature_key.toLowerCase().includes(kw)) ||
                (i.feature_name && i.feature_name.toLowerCase().includes(kw))
            )
        }
        this.features = list
    }

    /* ===========================================================
     *                          表格辅助
     * =========================================================== */

    private typeLabel(code: string) {
        const t = this.providerTypes.find(i => i.code === code)
        return t ? t.label : code
    }

    private typeTagType(code: string) {
        return this.typeTagMap[code] !== undefined ? this.typeTagMap[code] : ''
    }

    private formatConfigSummary(jsonStr: string): string {
        if (!jsonStr) return ''
        try {
            const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
            if (!obj || typeof obj !== 'object') return ''
            const parts: string[] = []
            Object.keys(obj).forEach(k => {
                const v = obj[k]
                if (v && typeof v === 'object') {
                    const sub = Object.keys(v).map(sk => `${sk}=${v[sk]}`).join(' ')
                    parts.push(`${k}: ${sub}`)
                } else {
                    parts.push(`${k}: ${v}`)
                }
            })
            return parts.join('，')
        } catch {
            return ''
        }
    }

    /* ===========================================================
     *                          筛选 / 分页
     * =========================================================== */

    private onSearch() {
        this.resetPage()
        this.getFeatures()
    }

    private onFilterChange() {
        this.resetPage()
        this.getFeatures()
    }

    private onCurrentChange(page: number) {
        this.pageIndex = page
        this.getFeatures()
    }

    private resetPage() {
        this.pageIndex = 1
    }

    /* ===========================================================
     *                          写类操作
     * =========================================================== */

    private onCreate() {
        this.editDialog.show('create')
    }

    private onEdit(row: IAiFeature) {
        this.editDialog.show('edit', row)
    }

    private async onSubmitCreate(params: IUpsertFeatureParams) {
        const result = await AiFeatureService.createFeature(params)
        if (result.status === 200) {
            this.$message.success('创建成功')
            this.editDialog.hide()
            this.resetPage()
            this.getFeatures()
        } else {
            this.$message.error(result.message || '创建失败')
            this.onSubmitDone()
        }
    }

    private async onSubmitUpdate(params: IUpsertFeatureParams) {
        const result = await AiFeatureService.updateFeature(params)
        if (result.status === 200) {
            this.$message.success('修改成功')
            this.editDialog.hide()
            this.getFeatures()
        } else {
            this.$message.error(result.message || '修改失败')
            this.onSubmitDone()
        }
    }

    /** 提交结束后恢复 submitting=false，让弹窗可以正常关闭 */
    private onSubmitDone() {
        // editDialog 在 update/create 失败时不会自动 hide，需要手动清 submitting
        (this.editDialog as any).submitting = false
    }

    private async onDelete(row: IAiFeature) {
        try {
            await this.$confirm(
                `确定删除「${row.feature_name}」吗？`,
                '提示',
                { type: 'warning' }
            )
        } catch {
            return
        }
        const result = await AiFeatureService.deleteFeature(row.id)
        if (result.status === 200) {
            this.$message.success('删除成功')
            this.getFeatures()
        } else {
            this.$message.error(result.message || '删除失败')
        }
    }

    /* ===========================================================
     *                          启停
     * =========================================================== */

    private async onToggleStatus(row: IAiFeature, status: number) {
        const result = await AiFeatureService.setFeatureStatus(row.id, status)
        if (result.status === 200) {
            this.$message.success(status === 1 ? '已启用' : '已禁用')
            row.status = status
        } else {
            this.$message.error(result.message || '操作失败')
        }
    }

    /* ===========================================================
     *                       全域排查
     * =========================================================== */

    /**
     * 点击"全域排查"按钮：弹出参数配置弹窗
     */
    private onBatchCheck() {
        // 重置表单为默认值
        this.batchConfigDialog.form = {
            batchSize: -1,
            maxMedia: -1
        }
        this.batchConfigDialog.visible = true
    }

    /**
     * 确认启动批量排查：调 /start 接口，关闭配置弹窗，打开进度弹窗
     */
    private async onStartBatchCheck() {
        // 表单校验
        const valid = await (this as any).$refs.batchConfigForm.validate().catch(() => false)
        if (!valid) return

        this.batchConfigDialog.loading = true

        try {
            const startRes = await AiBatchCheckService.startBatchCheck({
                batchSize: this.batchConfigDialog.form.batchSize,
                maxMedia: this.batchConfigDialog.form.maxMedia
            })

            if (startRes.status === 200) {
                // 关闭配置弹窗
                this.batchConfigDialog.visible = false
                // 打开进度弹窗
                this.batchProgressDialog.visible = true
                // 初始化 task
                this.applyTaskToDialog(startRes.data || {})
                // 开始轮询
                this.startPolling()
                this.$message.success('批量排查已启动')
            } else if (startRes.status === 400 && /已有/.test(startRes.message || '')) {
                // 已有任务在跑：打开进度弹窗
                this.batchConfigDialog.visible = false
                this.batchProgressDialog.visible = true
                this.$message.info('已有排查任务进行中，正在连接进度…')
                this.startPolling()
            } else {
                this.$message.error(startRes.message || '启动失败')
            }
        } finally {
            this.batchConfigDialog.loading = false
        }
    }

    /** 用户点停止：调 /stop，弹窗态更新到 STOPPED 后由轮询统一收敛 */
    private async onStopBatchCheck() {
        this.batchProgressDialog.stopping = true
        const res = await AiBatchCheckService.stopBatchCheck()
        this.batchProgressDialog.stopping = false
        if (res.status === 200) {
            this.$message.success('已发送停止指令，已开始的节点将跑完')
        } else {
            this.$message.error(res.message || '停止失败')
        }
        // 不断掉轮询：让进度自然收敛到 STOPPED
    }

    /** 进度弹窗关闭的统一出口（before-close 也会走这里） */
    private onBatchProgressDialogClose(done: Function) {
        this.clearPolling()
        done()
    }

    /** 开始轮询 */
    private startPolling() {
        if (this.pollTimer) return
        this.pollTimer = window.setInterval(this.pollOnce, POLL_INTERVAL_MS)
        // 立即拉一次，避免等 2s 才看到第一帧
        this.pollOnce()
        // 启动本地时间 ticker（与后端轮询解耦，秒级更新）
        this.startTick()
    }

    /** 停止轮询（幂等） */
    private clearPolling() {
        if (this.pollTimer !== null) {
            window.clearInterval(this.pollTimer)
            this.pollTimer = null
        }
        this.clearTick()
    }

    /** 启动 1s 一次的本地 ticker：根据 startTime 实时计算已用时间 */
    private startTick() {
        if (this.tickTimer) return
        // 立即算一次，避免首帧 0 秒
        this.tickElapsed()
        this.tickTimer = window.setInterval(this.tickElapsed, 1000)
    }

    /** 停止 ticker（幂等） */
    private clearTick() {
        if (this.tickTimer !== null) {
            window.clearInterval(this.tickTimer)
            this.tickTimer = null
        }
    }

    /** ticker 回调：仅当任务仍在运行且 startTime 有效时才更新 elapsedSec */
    private tickElapsed() {
        const t = this.batchProgressDialog.task
        // 任务进入终态后冻结时间
        if (t.status === 'COMPLETED' || t.status === 'STOPPED' || t.status === 'FAILED') return
        if (!t.startTime) return
        const sec = Math.max(0, Math.floor((Date.now() - t.startTime) / 1000))
        // 用 $set 保持响应式更新（深嵌套字段在 Vue 2 class 组件中的最佳实践）
        this.$set(t, 'elapsedSec', sec)
    }

    /** 单次轮询 progress */
    private async pollOnce() {
        const res = await AiBatchCheckService.getProgress()
        if (res.status !== 200) {
            // 网络/权限错误：把消息放进弹窗但不打断轮询
            this.batchProgressDialog.errorMsg = res.message || '获取进度失败'
            return
        }
        // data 为 null 表示当前没任务
        if (!res.data) {
            // 若我们已经显示过 RUNNING，突然变 null，说明后端状态被清空，停止轮询
            this.clearPolling()
            return
        }
        this.applyTaskToDialog(res.data)
        const s = res.data.status
        if (s === 'COMPLETED' || s === 'STOPPED' || s === 'FAILED') {
            this.clearPolling()
        }
    }

    /** 把接口返回的 task 平铺到弹窗 state（同时防御 null/undefined） */
    private applyTaskToDialog(t: any) {
        this.batchProgressDialog.task = {
            taskId: t.taskId || '',
            total: typeof t.total === 'number' ? t.total : 0,
            pending: typeof t.pending === 'number' ? t.pending : 0,
            success: typeof t.success === 'number' ? t.success : 0,
            failed: typeof t.failed === 'number' ? t.failed : 0,
            skipped: typeof t.skipped === 'number' ? t.skipped : 0,
            failedDetails: Array.isArray(t.failedDetails) ? t.failedDetails : [],
            stopFlag: !!t.stopFlag,
            startTime: typeof t.startTime === 'number' ? t.startTime : 0,
            status: t.status || '',
            elapsedSec: typeof t.elapsedSec === 'number' ? t.elapsedSec : 0,
            etaSec: typeof t.etaSec === 'number' ? t.etaSec : 0
        }
        this.batchProgressDialog.errorMsg = ''
    }

    /** 完成百分比 0~100，total=0 时返回 0 */
    private get progressPercent(): number {
        const t = this.batchProgressDialog.task
        if (!t.total || t.total <= 0) return 0
        const done = t.total - t.pending
        const p = Math.floor((done / t.total) * 1000) / 10
        return Math.max(0, Math.min(100, p))
    }

    /** 进度条状态（success=绿、warning=黄、exception=红），返回 undefined 时不传 prop */
    private get progressStatus(): 'success' | 'warning' | 'exception' | undefined {
        const s = this.batchProgressDialog.task.status
        if (s === 'COMPLETED') return 'success'
        if (s === 'FAILED') return 'exception'
        if (s === 'STOPPED') return 'warning'
        return undefined
    }

    /** 状态 → 中文 label */
    private statusLabel(status: string): string {
        if (!status) return '未启动'
        if (status === 'RUNNING') return '运行中'
        if (status === 'COMPLETED') return '已完成'
        if (status === 'STOPPED') return '已停止'
        if (status === 'FAILED') return '异常终止'
        return status
    }

    /** 状态 → tag 类型 */
    private statusTagType(status: string): '' | 'primary' | 'success' | 'info' | 'danger' {
        if (!status) return ''
        if (status === 'RUNNING') return 'primary'
        if (status === 'COMPLETED') return 'success'
        if (status === 'STOPPED') return 'info'
        if (status === 'FAILED') return 'danger'
        return ''
    }

    private get isRunning(): boolean {
        return this.batchProgressDialog.task.status === 'RUNNING'
    }

    /** 整数秒 → "1 分 23 秒" / "12 秒" */
    private formatDuration(sec: number): string {
        if (!sec || sec < 0) return '0 秒'
        const s = Math.floor(sec)
        if (s < 60) return `${s} 秒`
        const m = Math.floor(s / 60)
        const r = s % 60
        return r === 0 ? `${m} 分` : `${m} 分 ${r} 秒`
    }

    /** 组件销毁时务必清理轮询，避免泄漏 */
    private beforeDestroy() {
        this.clearPolling()
    }
}
